import * as types from '../../constants/actionTypes';
import * as trackingTypes from '../../actions/analyticsActions';
import analytics from '@finn-no/analytics';
import { trackPageView, trackEvent } from '@finn-no/pulse-sdk';
import { createAPIUrl } from '../../utils/createUrl';

/*
 * Sending an event to Adobe.
 * Events are to be triggered for user interactions, such as a click of a button or submitting a form.
 * */
function _event(name, flags, kpi) {
    /* REGEX to replace camelCase to kebab-case */
    const eventName = name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

    analytics.trackEvent({
        analyticsName: eventName,
        eventFlags: flags,
        kpi,
        context: {
            vertical: 'job',
            site: 'finn.no/jobb',
        },
    });
}

/* Sending an event to both Adobe & Event Collector */
function _eventAndEC(name, flags, adId, source) {
    /* REGEX with global flag which replace all occurrences
     of '-' with '_' to support EC-name-format: "[A-Za-z0-9_\\.]+"; */
    const ecEventName = name.replace(/-/g, '_');

    analytics.trackEvent({
        analyticsName: name,
        eventFlags: flags,
        stringValue: source,
        ecType: 'USER',
        ecCategory: ecEventName,
        context: {
            finnkode: adId,
            vertical: 'job',
            site: 'finn.no/jobb',
        },
    });
}

/* Sending an event only to Event Collector */
function _ec(ecType, ecCategory, id) {
    analytics.trackEvent({
        ecType,
        ecCategory,
        context: {
            finnkode: id,
            vertical: 'job',
            site: 'finn.no/jobb',
        },
    });
}

/* Track Page */
function _page(param) {
    const eventObj = param || {};
    eventObj.context = eventObj.context || {};
    eventObj.context.site = 'finn.no';
    analytics.trackPage(eventObj);
}

const updateFieldMap = {
    companyName: 'company-name',
    description: 'company-description',
};

const eventHandlers = {
    [types.CREATE_PROFILE_SUCCESS]: (action) => {
        _event('register.create-company-profile', 'start', 'activity');
        trackEvent({
            type: 'Click',
            intent: 'Create',
            name: 'Company profile',
            object: {
                id: 'company_profile_create_click',
                type: 'UIElement',
                elementType: 'Button',
            },
            target: {
                id: action.result['_links']['emp:profile:public'][0].href,
                type: 'UserProfilePage',
                pageType: 'company_profile',
                name: action.companyName,
            },
        });
    },
    [types.FOLLOW_COMPANY]: () => {
        _event('connect.follow-company', 'start,success', 'activity');
        trackEvent({
            type: 'Click',
            intent: 'Follow',
            name: 'Follow company',
            object: {
                id: 'company_profile_follow_click',
                type: 'UIElement',
                elementType: 'Button',
            },
            target: {
                id: 'id',
                type: 'SavedList',
            },
        });
    },
    [types.UNFOLLOW_COMPANY]: () => {
        _event('administrate.unfollow-company', 'start,success');
        trackEvent({
            type: 'Click',
            intent: 'Unfollow',
            name: 'Unfollow company',
            object: {
                id: 'company_profile_unfollow_click',
                type: 'UIElement',
                elementType: 'Button',
            },
            target: {
                id: 'id',
                type: 'SavedList',
            },
        });
    },
    [types.UPLOAD_PROFILE_BACKGROUND]: () =>
        _event('administrate.change-background-image', 'start,success'),
    [types.UPLOAD_PROFILE_LOGO]: () =>
        _event('administrate.change-logo', 'start,success'),
    [types.UPLOAD_IMAGE]: () =>
        _event('administrate.add-image', 'start,success'),
    [types.UPDATE_IMAGE]: () =>
        _event('administrate.change-image-description', 'start,success'),
    [types.REMOVE_IMAGE]: () =>
        _event('administrate.remove-image', 'start,success'),
    [types.REORDER_IMAGE]: () =>
        _event('administrate.reorder-image', 'start,success'),
    [types.ADD_VIDEO]: () => _event('administrate.add-video', 'start,success'),
    [types.UPDATE_VIDEO]: () =>
        _event('administrate.change-video-description', 'start,success'),
    [types.DELETE_VIDEO]: () =>
        _event('administrate.remove-video', 'start,success'),
    [types.PUBLISH_PROFILE]: () => {
        _event('register.create-company-profile.done', 'success', 'activity');
        trackEvent({
            type: 'Click',
            intent: 'Save',
            name: 'Company profile',
            object: {
                id: 'company_profile_publish_click',
                type: 'UIElement',
                elementType: 'Button',
            },
        });
    },
    [types.ADD_AMBASSADOR]: () =>
        _event('administrate.add-ambassador', 'start,success'),
    [types.DELETE_AMBASSADOR]: () =>
        _event('administrate.remove-ambassador', 'start,success'),
    [types.EDIT_AMBASSADOR]: () =>
        _event('administrate.change-ambassador', 'start,success'),

    // Pure tracking
    [trackingTypes.PAGE_VIEW]: () => _page(),
    [trackingTypes.UPDATE_FIELD]: (action) =>
        _event(
            `administrate.change-${updateFieldMap[action.name]}`,
            'start,success'
        ),
    [trackingTypes.CANT_FIND_COMPANY]: () =>
        _event('get-help.can-not-find-my-company', 'start,success'),
    [trackingTypes.VIEW_PROFILE]: (action) => {
        _ec('EMPLOYER_PROFILE', 'ViewProfile', action.id);
        trackPageView({
            name: 'Employer profile',
            object: {
                type: 'Page',
                pageType: 'company_profile',
            },
            vertical: {
                name: 'job',
            },
        });
    },
    [trackingTypes.VIEW_PROFILE_LIST]: () => {
        trackPageView({
            object: {
                type: 'Listing',
            },
            vertical: {
                name: 'job',
            },
        });
    },
    [trackingTypes.VIEW_EDIT_PROFILE]: (action) => {
        _ec('EMPLOYER_PROFILE', 'ViewEditProfile', action.id);
        trackPageView({
            object: {
                type: 'UserProfilePage',
                pageType: 'company_profile',
            },
            vertical: {
                name: 'business_center',
                subVertical: 'job',
            },
        });
    },
    [trackingTypes.VIEW_ONBOARDING]: () => {
        trackPageView({
            object: {
                type: 'UserProfilePage',
                pageType: 'company_profile',
            },
            vertical: {
                name: 'business_center',
                subVertical: 'job',
            },
        });
    },
    [trackingTypes.VIEW_STATISTICS]: (action) => {
        _ec('EMPLOYER_PROFILE', 'ViewStatistics', action.id);
        trackPageView({
            object: {
                type: 'UserProfilePage',
                pageType: 'tool',
            },
            vertical: {
                name: 'business_center',
                subVertical: 'job',
            },
        });
    },
    [trackingTypes.VIEW_SETTINGS]: (action) => {
        _ec('EMPLOYER_PROFILE', 'ViewSettings', action.id);
        trackPageView({
            object: {
                type: 'UserProfilePage',
                pageType: 'company_profile',
            },
            vertical: {
                name: 'business_center',
                subVertical: 'job',
            },
        });
    },
    [trackingTypes.EDIT_CLICK]: ({ profile }) => {
        _event('administrate.edit-profile', 'start,success');
        trackEvent({
            type: 'Click',
            intent: 'Edit',
            name: 'Company profile',
            object: {
                id: 'company_profile_edit_click',
                type: 'UIElement',
                elementType: 'Button',
            },
            target: {
                id: createAPIUrl(`/company/${profile.id}`),
                type: 'UserProfilePage',
                pageType: 'company_profile',
                name: profile.companyName,
            },
        });
    },
    [trackingTypes.PREVIEW_CLICK]: () =>
        _event('administrate.preview-profile', 'start,success'),
    [trackingTypes.VIDEO_STATUS]: (action) =>
        _event(
            `show-interest.${action.status}.${action.source}`,
            'start,success',
            'activity'
        ),
    [trackingTypes.VIEW_IMAGE]: () =>
        _event('show-interest.view-image', 'start,success'),
    [trackingTypes.SHOW_MORE_IMAGES]: () =>
        _event('show-interest.view-more-images', 'start,success'),
    [trackingTypes.EXPAND_AMBASSADORS]: () =>
        _event('profile.expand-ambassador', 'start', 'activity'),
    [trackingTypes.REVEAL_AMBASSADORS]: () =>
        _event('profile.reveal-ambassador', 'start', 'activity'),
    [trackingTypes.CLICK_ACTIVE_AD]: (action) => {
        _eventAndEC('show-interest.go-to-ad', 'start,success', action.adId);
        trackEvent({
            type: 'Click',
            intent: 'Open',
            name: 'Ads from this organization',
            object: {
                id: 'ads_from_this_organization',
                type: 'UIElement',
                elementType: 'Link',
            },
            target: {
                id: action.adId,
                category: 'n/a',
                type: 'ClassifiedAd',
                name: action.heading,
                contentId: action.adId,
            },
            vertical: {
                name: 'job',
            },
        });
    },
};

const analyticsMiddleware = () => (next) => (action) => {
    if (!__CLIENT__) {
        return next(action);
    }
    if (eventHandlers[action.type]) {
        eventHandlers[action.type](action);
    }
    return next(action);
};

export default analyticsMiddleware;
